import {Component, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ManagedSettingsService} from '../../services/managed-settings.service';
import * as moment from 'moment';
import {Moment} from 'moment';

@Component({
  selector: 'portal-notification-pause-dialog',
  templateUrl: './notification-pause-dialog.component.html',
  styleUrls: ['./notification-pause-dialog.component.scss']
})
export class NotificationPauseDialogComponent implements OnInit {
  readonly settingKey = 'notificationPauseUntil';
  readonly unpausedValue = '1980-05-07T00:00:00.000Z';
  readonly minimumPausedYear = 1990;

  pauseDate: Moment | null = null;
  pauseTime = '';
  currentValue = '';
  loading = false;
  saving = false;

  get currentValueLabel(): string {
    if (!this.currentValue || this.isUnpausedValue(this.currentValue)) {
      return 'Not paused';
    }

    return this.currentValue;
  }

  constructor(
    private dialogRef: MatDialogRef<NotificationPauseDialogComponent>,
    private settings: ManagedSettingsService,
    private popup: MatSnackBar
  ) {
  }

  ngOnInit(): void {
    this.loadValue();
  }

  close(): void {
    this.dialogRef.close(false);
  }

  savePause(): void {
    if (!this.pauseDate || !this.pauseTime) {
      return;
    }

    const isoTimestamp = this.toIsoTimestamp(this.pauseDate, this.pauseTime);
    if (!isoTimestamp) {
      this.popup.open('Please enter a valid date/time', 'OK', {duration: 4000, panelClass: ['failure']});
      return;
    }

    this.saving = true;
    this.settings.setValue(this.settingKey, isoTimestamp).subscribe({
      next: () => {
        this.saving = false;
        this.popup.open('Notification pause updated', 'OK', {duration: 3000, panelClass: ['success']});
        this.dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
        this.popup.open('Error updating notification pause', 'OK', {duration: 4000, panelClass: ['failure']});
      }
    });
  }

  clearPause(): void {
    this.saving = true;
    this.settings.setValue(this.settingKey, this.unpausedValue).subscribe({
      next: () => {
        this.saving = false;
        this.pauseDate = null;
        this.pauseTime = '';
        this.currentValue = this.unpausedValue;
        this.popup.open('Notifications unpaused', 'OK', {duration: 3000, panelClass: ['success']});
        this.dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
        this.popup.open('Error clearing notification pause', 'OK', {duration: 4000, panelClass: ['failure']});
      }
    });
  }

  private loadValue(): void {
    this.loading = true;
    this.settings.getValue(this.settingKey).subscribe((value) => {
      this.loading = false;
      if (!value || value === 'error retrieving content') {
        return;
      }

      this.currentValue = value;
      const loadedDateTime = this.fromServerValue(value);
      this.pauseDate = loadedDateTime.date;
      this.pauseTime = loadedDateTime.time;
    });
  }

  private fromServerValue(value: string): { date: Moment | null, time: string } {
    if (this.isUnpausedValue(value)) {
      return {date: null, time: ''};
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return {
        date: moment(value, 'YYYY-MM-DD'),
        time: '00:00'
      };
    }

    const parsed = moment(value, moment.ISO_8601, true);
    if (!parsed.isValid()) {
      return {date: null, time: ''};
    }

    return {
      date: parsed,
      time: parsed.format('HH:mm')
    };
  }

  private toIsoTimestamp(date: Moment, time: string): string | null {
    const match = /^(\d{2}):(\d{2})$/.exec(time);
    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {
      return null;
    }

    return date.clone()
      .hour(hours)
      .minute(minutes)
      .second(0)
      .millisecond(0)
      .toISOString();
  }

  private isUnpausedValue(value: string): boolean {
    if (!value) {
      return true;
    }

    const parsed = moment(value, [moment.ISO_8601, 'YYYY-MM-DD'], true);
    if (!parsed.isValid()) {
      return false;
    }

    return parsed.clone().utc().year() < this.minimumPausedYear;
  }
}


